// eslint-disable-next-line no-unused-vars
import React, { Suspense } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import Navbar from "src/components/ui-components/Navbar";
import Marquee from "src/components/ui-elements/Marquee";
import ToastProvider from "./components/ToastProvider";
import { fetchGlobalData } from "./services/GlobalApi";
import Loader from "./components/Loader";
import { fetchAllProducts } from "./services/ProductApi";
import { setProductStatusRedux } from "./helpers/productHelpers";
import { loadCategories } from "./helpers/categoryHelper";
import { loadBrands } from "./helpers/brandHelper";
import { loadCollections } from "./helpers/collectionHelper";
import Footer from "./components/ui-components/Footer";
import { fetchCountryList } from "./services/CountryApi";
import { useSelector } from "react-redux";
import { fetchPriceRange } from "./services/PriceRangeApi";
import { safeApiCall, setShopId } from "./api/axiosInstance";
import WhatsAppButton from "./components/WhatsAppButton";
import TawkToWidget from "./components/TawkToWidget";
import FacebookPixel from "./components/FacebookPixel";
import useFacebookPageView from "./hooks/useFacebookPageView";
import { fetchTrendingProducts } from "./services/TrendingProductApi";
import CartDrawer from "./components/ui-components/Drawer/CartDrawer";
import { updateLocalCartFromServerOnLoad } from "./helpers/cartHelper";
import PageViewTracker from "./components/PageViewTracker";
import { fetchOrderReviews } from "./services/OrderReviewsApi";
import { fetchStorePages } from "./services/StorePagesApi";
import { fetchFeaturedBlogs } from "./services/BlogApi";
import { ShopIdProvider, useShopId } from "./contexts/ShopIdContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import useFontConfig from "./hooks/useFontConfig";
// eslint-disable-next-line no-unused-vars
import GlobalSEO from "./components/GlobalSEO";

// Lazy load page components for code splitting
const Home = React.lazy(() => import("src/pages/Home"));
const About = React.lazy(() => import("src/pages/About"));
const AllCategoryProducts = React.lazy(() => import("src/pages/Category/all"));
const DynamicCategory = React.lazy(() => import("./pages/Category/DynamicCategory"));
const BrandListing = React.lazy(() => import("./pages/Brand"));
const ProductDetails = React.lazy(() => import("./pages/Product/ProductDetails"));
const PolicyDetail = React.lazy(() => import("./pages/PolicyDetail"));
const Login = React.lazy(() => import("./pages/Login"));
const ContactUs = React.lazy(() => import("src/pages/ContactUs"));
const ProductCategoryMain = React.lazy(() => import("./pages/Category/ProductCategory"));
const CollectionCategoryMain = React.lazy(() => import("./pages/Category/CollectionCategory"));
const MyOrders = React.lazy(() => import("./pages/MyOrders"));
const OrderDetail = React.lazy(() => import("./pages/OrderDetail"));
const ShoppingCart = React.lazy(() => import("./pages/Cart"));
const CheckoutPage = React.lazy(() => import("./pages/Checkout"));
const StorePageDetail = React.lazy(() => import("./pages/Home/StorePageDetail"));
const BlogDetail = React.lazy(() => import("./components/ui-components/Blog/BlogDetail"));
const AllBlogs = React.lazy(() => import("./components/ui-components/Blog/AllBlogs"));
const StripeSuccess = React.lazy(() => import("./pages/Stripe/StripeSuccess"));
const StripeCancel = React.lazy(() => import("./pages/Stripe/StripeCancel"));
const ServicesPage = React.lazy(() => import("./pages/Services"));
const FaqPage = React.lazy(() => import("src/pages/Faq"));



// Layout component with Navbar and Marquee
const MainLayout = ({ children, paginationComponent }) => {
  const { pathname } = useLocation();
  const globalData = useSelector((state) => state.globalData.globalDataName);
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);

  const toggleCartDrawer = useCallback(() => {
    setIsCartDrawerOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    // Instant, not smooth: a route change can be an in-place param swap
    // (e.g. one product page to another via "You may also like", which
    // doesn't remount this layout) where the content height changes right
    // as/after this fires. A multi-frame smooth scroll can get clamped
    // mid-animation by that resize and never reach the top; a single
    // synchronous jump can't be interrupted that way.
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname]);

  const bannerText = globalData.banner || "";
  const marqueeData = bannerText.trim()
    ? bannerText
        .split(".")
        .flatMap((segment) => segment.split("\n"))
        .map((line) => line.trim())
        .filter((line) => line !== "")
    : [];

  return (
    <>
      {marqueeData.length > 0 && <Marquee data={marqueeData} />}
      <Navbar toggleCartDrawer={toggleCartDrawer} />
      {React.Children.map(children, child =>
        React.cloneElement(child, { openCartDrawer: toggleCartDrawer })
      )}
      <CartDrawer isOpen={isCartDrawerOpen} onClose={toggleCartDrawer} />
      <div
        style={
          paginationComponent && {
            marginTop: "clamp(30px, 5vw, 100px)",
          }
        }
      >
        <Footer />
      </div>
    </>
  );
};

// AppContent component that handles data fetching with shopId
const AppContent = () => {
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(false);
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [brands, setBrands] = useState([]);
  const accessToken = useSelector((state) => state.accessToken.token);
  const { updateShopId, setShopIdError, setShopIdLoading } = useShopId();

  useFontConfig();
  useFacebookPageView();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setShopIdLoading(true);

        // First, fetch global data to get shopId
        const shopId = await fetchGlobalData();

        if (shopId) {
          // Set the shopId for all subsequent API calls
          setShopId(shopId);
          updateShopId(shopId);

          // Fetch only critical data for initial render
          await Promise.all([
            safeApiCall(() => fetchAllProducts(), []),
            safeApiCall(() => setProductStatusRedux(), null),
            safeApiCall(() => loadCategories(setCategories), []),
            safeApiCall(() => loadCollections(setCollections), []),
            safeApiCall(() => loadBrands(setBrands), []),
            safeApiCall(() => fetchCountryList(), []),
            safeApiCall(() => fetchPriceRange(), {}),
            safeApiCall(() => updateLocalCartFromServerOnLoad(accessToken), []),
          ]);

          // Defer non-critical data loading until after initial render
          setTimeout(() => {
            safeApiCall(() => fetchTrendingProducts(), []);
            safeApiCall(() => fetchOrderReviews(), []);
            safeApiCall(() => fetchStorePages(), []);
            safeApiCall(() => fetchFeaturedBlogs(1, 10), []);
          }, 0);
        } else {
          throw new Error("Failed to get shopId from global data");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoadingError(true);
        setShopIdError("Failed to initialize application");
      } finally {
        setLoading(false);
        setShopIdLoading(false);
      }
    };

    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <Loader />;
  }

  if (loadingError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
        <p className="mb-4">We're having trouble loading the application</p>
        <button
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <>
      <GlobalSEO />
      <WhatsAppButton />
      <TawkToWidget />
      <FacebookPixel />
      <ToastProvider>
      <PageViewTracker />
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* Routes with main layout */}
            <Route
              path="/"
              element={
                <MainLayout>
                  <Home categories={categories} collections={collections} brands={brands} />
                </MainLayout>
              }
            />
            <Route
              path="/about"
              element={
                <MainLayout>
                  <About />
                </MainLayout>
              }
            />
            <Route
              path="/login"
              element={
                <MainLayout>
                  <Login />
                </MainLayout>
              }
            />
            <Route
              path="/category/all"
              element={
                <MainLayout paginationComponent={true}>
                  <AllCategoryProducts />
                </MainLayout>
              }
            />

            <Route
              path="/services"
              element={
                <MainLayout paginationComponent={true}>
                  <ServicesPage />
                </MainLayout>
              }
            />
            <Route
              path="/faq"
              element={
                <MainLayout>
                  <FaqPage />
                </MainLayout>
              }
            />
            <Route
              path="/category/:categoryName"
              element={
                <MainLayout paginationComponent={true}>
                  <DynamicCategory />
                </MainLayout>
              }
            />

            <Route
              path="/brand/:brandName"
              element={
                <MainLayout paginationComponent={true}>
                  <BrandListing />
                </MainLayout>
              }
            />

            <Route
              path="/productcategory/:productcategoryName"
              element={
                <MainLayout paginationComponent={true}>
                  <ProductCategoryMain />
                </MainLayout>
              }
            />

            <Route
              path="/collection/:collectionSlug"
              element={
                <MainLayout paginationComponent={true}>
                  <CollectionCategoryMain />
                </MainLayout>
              }
            />

            <Route
              path="/product/:slug"
              element={
                <MainLayout>
                  <ProductDetails />
                </MainLayout>
              }
            />

            <Route
              path="/page/:slug"
              element={
                <MainLayout>
                  <StorePageDetail />
                </MainLayout>
              }
            />

             <Route
              path="/blogs"
              element={
                <MainLayout paginationComponent={true}>
                  <AllBlogs />
                </MainLayout>
              }
            />

            <Route
              path="/blog/:slug"
              element={
                <MainLayout>
                  <BlogDetail />
                </MainLayout>
              }
            />


            {/* Standalone routes without the main layout */}
            <Route path="/policies/:name" element={<PolicyDetail />} />

            {/* Fallback route */}
            <Route
              path="*"
              element={
                <MainLayout>
                  <ContactUs />
                </MainLayout>
              }
            />
            <Route
              path="/orders"
              element={
                <MainLayout paginationComponent={true}>
                  <MyOrders />
                </MainLayout>
              }
            />
            <Route
              path="/order-detail/:orderId"
              element={
                <MainLayout>
                  <OrderDetail />
                </MainLayout>
              }
            />

            <Route
              path="/cart"
              element={
                <MainLayout paginationComponent={true}>
                  <ShoppingCart />
                </MainLayout>
              }
            />

            <Route
              path="/checkout"
              element={
                <MainLayout paginationComponent={true}>
                  <CheckoutPage />
                </MainLayout>
              }
            />

            {/* Stripe Payment Routes */}
            <Route
              path="/stripe/success/:orderUuid"
              element={
                <MainLayout>
                  <StripeSuccess />
                </MainLayout>
              }
            />
            <Route
              path="/stripe/cancel/:orderUuid"
              element={
                <MainLayout>
                  <StripeCancel />
                </MainLayout>
              }
            />
          </Routes>
        </Suspense>
      </ToastProvider>
    </>
  );
};

// Main App component with ShopIdProvider and ThemeProvider
function App() {
  return (
    <ShopIdProvider>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </ShopIdProvider>
  );
}
export default App;
